import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { MIN_PASSWORD_LENGTH } from '@diana/shared'
import { api } from '../../lib/api.js'
import { EntryCard } from '../../classroom/EntryCard.js'
import { Button, FieldLabel, Skeleton } from '../../classroom/ui.js'

/**
 * The first thing a student ever sees. They arrive from a link Diana sent
 * them by Telegram or WhatsApp, choose a password, and land in their own
 * area already signed in.
 *
 * Deliberately outside the locale prefix and outside both layouts: no
 * navigation, nothing to click away to, one job.
 */
export function InvitePage() {
  const { token = '' } = useParams()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const invite = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api.invite.peek(token),
    retry: false,
  })

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (password !== confirm) {
      setError('Hasła nie są takie same.')
      return
    }
    setBusy(true)
    setError('')
    try {
      const res = await api.invite.redeem(token, password)
      /*
       * A full page load, not navigate(): this tab's session state still says
       * "signed out", and the panel would read that and bounce to /login. If
       * the server could not sign them in, the password is set all the same —
       * /login is the honest next page.
       */
      window.location.replace(res.signedIn === false ? '/login' : '/panel')
    } catch {
      // The server answers the same way for expired, used and never-existed,
      // so this message must not guess which one it was.
      setError('Ten link już nie działa. Poproś Dianę o nowy.')
      setBusy(false)
    }
  }

  if (invite.isPending) {
    return (
      <EntryCard>
        <Skeleton lines={3} />
      </EntryCard>
    )
  }

  if (invite.isError) {
    return (
      <EntryCard>
        <h1 className="cl-heading m-0">Ten link już nie działa</h1>
        <p className="m-0 mt-2 text-class-ink-soft">
          Zaproszenia są jednorazowe i ważne dwa tygodnie. Napisz do Diany — wyśle Ci nowe.
        </p>
      </EntryCard>
    )
  }

  return (
    <EntryCard>
      <p className="m-0 text-class-label font-semibold text-class-ink-faint">Zaproszenie</p>
      <h1 className="cl-title m-0 mt-1.5">Cześć, {invite.data.name}!</h1>
      <p className="m-0 mt-3 text-class-ink-soft">
        Ustaw hasło do swojego konta. Będziesz się logować adresem <strong className="font-semibold text-class-ink">{invite.data.email}</strong>.
      </p>

      <form onSubmit={handleSubmit} className="mt-7 flex flex-col gap-4">
        <div>
          <FieldLabel htmlFor="password">Nowe hasło</FieldLabel>
          <input
            id="password"
            type="password"
            required
            minLength={MIN_PASSWORD_LENGTH}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="cl-input"
          />
          <p className="m-0 mt-1.5 text-class-label text-class-ink-faint">Co najmniej {MIN_PASSWORD_LENGTH} znaków.</p>
        </div>
        <div>
          <FieldLabel htmlFor="confirm">Powtórz hasło</FieldLabel>
          <input
            id="confirm"
            type="password"
            required
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="cl-input"
          />
        </div>

        {error && <p role="alert" className="m-0 font-semibold text-class-wrong">✗ {error}</p>}

        <Button type="submit" disabled={busy} className="mt-2 w-full">
          {busy ? 'Zapisuję…' : 'Ustaw hasło i wejdź'}
        </Button>
      </form>
    </EntryCard>
  )
}
